/* storage.js — every localStorage read/write for the app.
   All keys are prefixed "cad." and each stores one JSON blob. */
(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  const KEYS = {
    settings: 'cad.settings',
    classification: 'cad.classification',
    files: 'cad.files',
    syncMeta: 'cad.syncMeta',
  };

  const DEFAULT_SETTINGS = {
    apiKey: '',
    startDateMode: 'preset', // 'all' | 'preset' | 'custom'
    startDate: null,         // 'YYYY-MM-DD' when mode is 'custom'
    presetDays: 30,
    autoSyncMinutes: 0,      // 0 = manual only
    createdAt: null,
    lastEditedAt: null,
  };

  const DEFAULT_SYNC_META = {
    initialBackfillComplete: false,
    watermarkNewestCreatedAt: null,
    lastSyncStartedAt: null,
    lastSyncCompletedAt: null,
    lastSyncStatus: 'idle', // idle | running | success | error
    lastError: null,
    totalFilesStored: 0,
  };

  // Embedded copy of classification.default.json so first-run bootstrap and
  // "Reset to default" still work when the app is opened via file://, where
  // fetch() of a sibling file is blocked by the browser.
  const EMBEDDED_DEFAULT = {
    version: '1.0',
    categories: [
      { id: 'personal', label: 'Personal', icon: 'user', color: '#7C3AED', defaultSensitivity: 'high',
        keywords: ['passport', 'ssn', 'social_security', 'national_id', 'driver_license', 'drivers_license', 'birth_certificate', 'personal', 'private', 'medical', 'health_record', 'prescription', 'insurance_personal', 'home_address', 'resume', 'cv_', 'bank_statement', 'tax_return', 'family_photo'] },
      { id: 'hr', label: 'HR', icon: 'users', color: '#DB2777', defaultSensitivity: 'high',
        keywords: ['offer_letter', 'payroll', 'salary', 'compensation', 'employee', 'hiring', 'recruit', 'candidate', 'hr_', 'performance_review', 'termination', 'onboarding', 'benefits', 'pto_', 'timesheet', 'org_chart', 'headcount', 'interview_notes'] },
      { id: 'legal', label: 'Legal', icon: 'scale', color: '#4F46E5', defaultSensitivity: 'high',
        keywords: ['contract', 'nda', 'non_disclosure', 'agreement', 'legal', 'lawsuit', 'litigation', 'terms_of_service', 'subpoena', 'patent', 'trademark', 'copyright', 'settlement', 'license_agreement', 'msa_', 'redline'] },
      { id: 'governance', label: 'Governance', icon: 'shield', color: '#0EA5E9', defaultSensitivity: 'medium',
        keywords: ['governance', 'audit', 'risk_register', 'controls', 'compliance_report', 'charter', 'policy', 'soc2', 'iso27001', 'gdpr', 'data_retention', 'incident_report', 'whistleblower'] },
      { id: 'business', label: 'Business', icon: 'briefcase', color: '#0D9488', defaultSensitivity: 'medium',
        keywords: ['proposal', 'strategy', 'roadmap', 'presentation', 'deck', 'business_plan', 'meeting_notes', 'okr', 'kpi', 'market_research', 'competitive_analysis', 'pitch', 'brief', 'memo'] },
      { id: 'financial', label: 'Financial', icon: 'dollar-sign', color: '#CA8A04', defaultSensitivity: 'high',
        keywords: ['invoice', 'budget', 'financial', 'finance', 'expense', 'tax', 'revenue', 'forecast', 'statement', 'payment', 'p&l', 'balance_sheet', 'cash_flow', 'receipt', 'purchase_order', 'quote'] },
      { id: 'technical', label: 'Technical', icon: 'code', color: '#64748B', defaultSensitivity: 'low',
        keywords: ['code', 'script', 'config', 'log', 'dataset', 'backup', 'source', '.sql', '.json', '.csv', '.py', '.ts', '.yaml', 'schema', 'api_spec', 'readme'] },
      { id: 'uncategorized', label: 'Uncategorized', icon: 'file-question', color: '#94A3B8', defaultSensitivity: 'low',
        keywords: [] },
    ],
    matching: {
      caseSensitive: false,
      matchType: 'substring',
      priorityOrder: ['personal', 'hr', 'legal', 'governance', 'financial', 'business', 'technical'],
      fallbackCategoryId: 'uncategorized',
    },
  };

  function readJSON(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return fallback;
      const parsed = JSON.parse(raw);
      return parsed == null ? fallback : parsed;
    } catch (err) {
      console.warn('[storage] Failed to parse "' + key + '", returning default.', err);
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      throw Object.assign(new Error('Browser storage quota exceeded. Clear old data from Settings, or reduce the history window and re-sync.'), { type: 'QUOTA_EXCEEDED', cause: err });
    }
  }

  function clone(obj) { return JSON.parse(JSON.stringify(obj)); }

  CAD.storage = {
    KEYS,

    // ---- settings ----
    getSettings() {
      return Object.assign({}, DEFAULT_SETTINGS, readJSON(KEYS.settings, {}));
    },
    saveSettings(patch) {
      const next = Object.assign(this.getSettings(), patch);
      const now = new Date().toISOString();
      if (!next.createdAt) next.createdAt = now;
      next.lastEditedAt = now;
      writeJSON(KEYS.settings, next);
      return next;
    },

    // ---- classification ----
    getClassification() {
      return readJSON(KEYS.classification, null);
    },
    saveClassification(obj) {
      writeJSON(KEYS.classification, obj);
    },
    async loadDefaultClassification() {
      try {
        const res = await fetch('classification.default.json');
        if (res.ok) return await res.json();
        console.warn('[storage] classification.default.json returned HTTP ' + res.status + ', using embedded default.');
      } catch (err) {
        console.warn('[storage] Could not fetch classification.default.json (file:// or offline), using embedded default.');
      }
      return clone(EMBEDDED_DEFAULT);
    },
    async ensureClassification() {
      if (!this.getClassification()) {
        this.saveClassification(await this.loadDefaultClassification());
      }
    },

    // ---- files ----
    getFiles() {
      return readJSON(KEYS.files, {});
    },
    saveFiles(files) {
      writeJSON(KEYS.files, files);
    },
    getFileList() {
      const files = this.getFiles();
      return Object.keys(files).map(function (id) { return files[id]; })
        .sort(function (a, b) { return new Date(b.createdAt) - new Date(a.createdAt); });
    },
    hasFile(id) {
      return Object.prototype.hasOwnProperty.call(this.getFiles(), id);
    },
    getFileCount() {
      return Object.keys(this.getFiles()).length;
    },
    upsertManyFiles(records) {
      const files = this.getFiles();
      for (let i = 0; i < records.length; i++) files[records[i].id] = records[i];
      this.saveFiles(files);
    },

    // ---- sync meta ----
    getSyncMeta() {
      return Object.assign({}, DEFAULT_SYNC_META, readJSON(KEYS.syncMeta, {}));
    },
    patchSyncMeta(patch) {
      writeJSON(KEYS.syncMeta, Object.assign(this.getSyncMeta(), patch));
    },

    // ---- misc ----
    approxStorageBytes() {
      let total = 0;
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.indexOf('cad.') === 0) {
            total += (key.length + (localStorage.getItem(key) || '').length) * 2; // UTF-16
          }
        }
      } catch (err) { /* ignore */ }
      return total;
    },
    clearAll() {
      const doomed = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf('cad.') === 0) doomed.push(key);
      }
      doomed.forEach(function (key) { localStorage.removeItem(key); });
    },
  };
})();
